import Meta from '@root/common/utils/Meta';
import articleConfig from '@root/modules/article/config/article.config';
import getArticleCustomStyles from '@root/modules/article/utils/getArticleCustomStyles';

import { GetChannelConfigFunction } from '@root/libs/init-channel/types';
import { Data as ArticleData, Meta as ArticleMeta, DataComments as CommentsArticleData } from '@root/modules/article/types/article';

import { getCustomCropsPerRatio, getFocuspointQueryString } from '@root/libs/api-provider/utils/pictureCustomCrops';
import { getEnv } from '@root/common/utils/getEnv';
import { stripTags } from '@root/common/filters/htmlFormater';
import buildArticleUrl from '@root/modules/article/utils/buildArticleUrl';
import rootConfig from '@root/config/root.config';

function getArticleMeta({
  $channelConfig,
  article,
  path,
}: {
  $channelConfig: GetChannelConfigFunction;
  article: ArticleData | CommentsArticleData | null;
  path: string;
}) {
  const meta = new Meta($channelConfig('meta').base, path);

  if (article) {
    const { id, publishAt, primaryCategory, tags, content } = article;
    const cssText = getArticleCustomStyles(article);
    const paywallEnabled = content.paywall.enabled || false;
    const authors = article.authors;

    // Generate article canonical URL with builder
    const canonical = buildArticleUrl({ article: article as ArticleData, domain: article.primaryCategory.channel.url, fakeDomainEnabled: false });
    const index = meta.link.findIndex((link) => link.rel === 'canonical');
    meta.link[index] = { rel: 'canonical', hid: 'canonical', href: rootConfig.main_portal_url + canonical };

    meta.title = article.seoFields.seoTitle || stripTags(article.content.title.html);
    meta.ogTitle = stripTags(article.content.title.html);
    meta.description = article.seoFields.seoDescription || stripTags(article.content.lead?.html) || '';
    meta.ogDescription = article.seoFields.seoDescription || stripTags(article.content.lead?.html) || '';
    meta.type = 'article';

    if (tags.items.length > 0) {
      const keywords = tags.items.map((tag) => tag.name).join(', ');
      meta.keywords = keywords;
    }

    if (article.metaImage) {
      const picture = article.metaImage;
      const customCrops = picture?.attributes?.cropperData?.customCrops;
      const customCropsPerRatio = getCustomCropsPerRatio(customCrops);
      const focuspoint = picture?.attributes?.cropperData?.focuspoint;
      const focusPointString = getFocuspointQueryString(focuspoint, article.id);

      const url = getEnv('mediaApiCropper');
      const src = `${url}/${picture.id}.jpg?w=1200&h=800${focusPointString}${customCropsPerRatio['16:9']}`;

      meta.imageData = {
        src,
        ...articleConfig.meta.imageData,
      };
    }

    meta.createArticleMeta({ id, publishAt, primaryCategory, lang: $channelConfig('settings').lang, paywallEnabled, authors });

    if (cssText) {
      meta.style = [{ cssText, hid: 'custom-article' }];
    }
  }

  return meta;
}

function replaceArticleMeta(newMeta: ArticleMeta) {
  const htmlMetaData = document.getElementsByTagName('meta');
  document.title = newMeta.title;

  for (const meta of newMeta.meta) {
    for (const htmlMeta of htmlMetaData) {
      if (htmlMeta.getAttribute('data-hid') === meta.hid) {
        htmlMeta.setAttribute('content', meta.content);
      }
    }
  }
}

export { getArticleMeta, replaceArticleMeta };
